import { toast, useLocalization, createMutation, createTriggerResource } from '@magicdoorinc/ui';
import { useSearchParams } from '@solidjs/router';
import { createEffect, createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { magicTagsRepository } from '~/repositories/magicTagsRepository';
import type { CustomFieldsFilter } from '~/repositories/magicTagsRepository';
import type {
  CreateCustomFieldDto,
  CustomFieldDto,
  CustomFieldPaginationDto,
  EntityCustomFieldDto,
  EntityCustomFieldsDto,
  EntityType,
  SetCustomFieldValueDto,
  UpdateCustomFieldDto,
} from '~/swagger/portal/Api';

export type KeyValuePair = Record<string, string>;

export const [MagicTagsProvider, useMagicTags] = createMagicDoorContext('MagicTags', () => {
  const { t } = useLocalization();

  const [editingTag, setEditingTag] = createSignal<EntityCustomFieldDto | null>(null);

  const [customFields, setFilter, { trigger: filter, refetch: refetchCustomFields, mutate: mutateCustomFields }] = createTriggerResource<
    CustomFieldsFilter,
    CustomFieldPaginationDto
  >((filter?: CustomFieldsFilter) => magicTagsRepository.getCustomFields(filter));

  const [customField, setCustomField] = createSignal<CustomFieldDto | null>(null);

  const createCustomFieldMutation = createMutation(async (customField: CreateCustomFieldDto) => {
    try {
      const response = await magicTagsRepository.createCustomField(customField);
      toast.success(t('Custom field has been created successfully'));
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      toast.error(t('Failed to create custom field: {{error}}', { error: errorMessage }));
      throw err;
    }
  });

  const updateCustomFieldMutation = createMutation(
    async ({ customFieldId, customField }: { customFieldId: string; customField: UpdateCustomFieldDto }) => {
      try {
        const response = await magicTagsRepository.updateCustomField(customFieldId, customField);
        toast.success(t('Custom field has been updated successfully'));
        refetchCustomFields();
        return response;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        toast.error(t('Failed to update custom field: {{error}}', { error: errorMessage }));
        throw err;
      }
    }
  );

  const deleteCustomFieldMutation = createMutation(async (customFieldId: string) => {
    try {
      await magicTagsRepository.deleteCustomField(customFieldId);
      toast.success(t('Custom field has been deleted successfully'));
      mutateCustomFields((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.filter((item) => item.id !== customFieldId),
        };
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      toast.error(t('Failed to delete custom field: {{error}}', { error: errorMessage }));
      throw err;
    }
  });

  const [entityCustomFields, setEntityCustomFieldsFilter, { trigger: entityCustomFieldsTrigger, refetch: refetchEntityCustomFields }] =
    createTriggerResource<{ entityType: EntityType; entityId: string }, EntityCustomFieldsDto>((params) =>
      magicTagsRepository.getEntityCustomFieldValues(params.entityType, params.entityId)
    );

  const updateEntityCustomFieldValueMutation = createMutation(
    async (params: { entityType: EntityType; entityId: string; customFieldId: string; value: SetCustomFieldValueDto }) => {
      try {
        await magicTagsRepository.updateEntityCustomFieldValue(params.entityType, params.entityId, params.customFieldId, params.value);
        toast.success(t('Custom field value has been set successfully'));
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        toast.error(t('Failed to set custom field value: {{error}}', { error: errorMessage }));
        throw err;
      }
    }
  );

  const removeEntityCustomFieldValueMutation = createMutation(
    async (params: { entityType: EntityType; entityId: string; customFieldId: string }) => {
      try {
        await magicTagsRepository.removeEntityCustomFieldValue(params.entityType, params.entityId, params.customFieldId);
        toast.success(t('Custom field value has been removed successfully'));
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        toast.error(t('Failed to remove custom field value: {{error}}', { error: errorMessage }));
        throw err;
      }
    }
  );

  const [_, setSearchParams] = useSearchParams<{ entityType: EntityType }>();

  createEffect(() => {
    if (filter()?.entityType) {
      return setSearchParams({ entityType: filter()?.entityType });
    }

    if (entityCustomFieldsTrigger()?.entityType) {
      return setSearchParams({ entityType: entityCustomFieldsTrigger()?.entityType });
    }
  });

  return {
    customFields,
    customField,

    filter,
    setFilter,

    createCustomFieldMutation,
    updateCustomFieldMutation,
    deleteCustomFieldMutation,

    entityCustomFields,
    setEntityCustomFieldsFilter,
    entityCustomFieldsTrigger,
    refetchEntityCustomFields,

    updateEntityCustomFieldValueMutation,
    removeEntityCustomFieldValueMutation,

    setCustomField,
    refetchCustomFields,

    editingTag,
    setEditingTag,
  };
});
